import { Link, type LinkProps } from '@tanstack/react-router'
import { Fragment, type ReactNode, useEffect, useRef } from 'react'
import { EcosystemsLogo } from '@/shared/ui/brand'
import { Icon, type IconName } from '@/shared/ui/icon'

// Маршрут раздела — из типизированного дерева роутера: опечатка в пути не
// доживает до рантайма.
type NavTo = NonNullable<LinkProps['to']>

export interface NavItem {
  to: NavTo
  icon: IconName
  label: string
  // Раздел-корень подсвечивается только на самом себе: префиксом он совпал бы с
  // любым маршрутом приложения.
  exact?: boolean
}

// Набор разделов каркаса. renderItem рисует раздел со своей спецификой (счётчик
// непрочитанных у инбокса); вернул null — рисуется обычная ссылка.
export interface NavSource {
  items: readonly NavItem[]
  renderItem?: ((item: NavItem, onNavigate: (() => void) | undefined) => ReactNode) | undefined
}

export function NavItemLink({
  item,
  onNavigate,
  ariaLabel,
  badge,
}: {
  item: NavItem
  onNavigate?: (() => void) | undefined
  ariaLabel?: string | undefined
  badge?: ReactNode
}) {
  return (
    <Link
      to={item.to}
      onClick={onNavigate}
      activeOptions={item.exact ? { exact: true } : {}}
      {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
      className="flex items-center gap-3 rounded-control px-3 py-2 text-sm text-text-secondary transition-colors hover:bg-bg-elevated hover:text-text-primary"
      activeProps={{ className: 'bg-bg-elevated text-text-primary' }}
    >
      <Icon name={item.icon} size={18} />
      <span className="flex-1">{item.label}</span>
      {badge}
    </Link>
  )
}

// Единый список разделов — реюз сайдбаром (десктоп) и drawer (мобайл). onNavigate
// закрывает drawer при переходе; в сайдбаре не передаётся.
export function NavSections({
  items,
  renderItem,
  onNavigate,
  ariaLabel,
}: NavSource & { onNavigate?: (() => void) | undefined; ariaLabel: string }) {
  return (
    <nav aria-label={ariaLabel} className="flex flex-1 flex-col gap-1 px-3">
      {items.map((item) => (
        <Fragment key={item.label}>
          {renderItem?.(item, onNavigate) ?? <NavItemLink item={item} onNavigate={onNavigate} />}
        </Fragment>
      ))}
    </nav>
  )
}

// Сайдбар (≥ md): разделы сверху, footer — место под постоянный индикатор
// (таймер-бар команды); у каркаса без него сайдбар кончается навигацией.
export function ShellSidebar({ items, renderItem, footer }: NavSource & { footer?: ReactNode }) {
  return (
    <aside className="hidden w-60 shrink-0 flex-col border-r border-border-default md:flex">
      <div className="min-h-0 flex-1 overflow-y-auto py-4">
        <NavSections items={items} renderItem={renderItem} ariaLabel="Разделы" />
      </div>
      {footer && <div className="mt-auto border-t border-border-default px-6 py-4">{footer}</div>}
    </aside>
  )
}

export function NavBurger({ onOpen }: { onOpen: () => void }) {
  return (
    <button
      type="button"
      aria-label="Меню"
      onClick={onOpen}
      className="-ml-2 shrink-0 p-2 text-text-secondary transition-colors hover:text-text-primary md:hidden"
    >
      <Icon name="menu" size={20} />
    </button>
  )
}

// Мобильный drawer (< md): вся навигация сайдбара. Профиль живёт в шапке (не
// дублируется). Оверлей слева, backdrop/Esc/навигация закрывают, фокус в панель
// (autoFocus запрещён).
export function NavDrawer({
  open,
  onClose,
  home,
  items,
  renderItem,
}: NavSource & { open: boolean; onClose: () => void; home: NavTo }) {
  const panelRef = useRef<HTMLDivElement>(null)
  const onKeyRef = useRef<(e: KeyboardEvent) => void>(() => {})

  useEffect(() => {
    onKeyRef.current = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
      }
    }
  })

  useEffect(() => {
    if (!open) return
    const el = panelRef.current
    el?.focus()
    const listener = (e: KeyboardEvent) => onKeyRef.current(e)
    el?.addEventListener('keydown', listener)
    // Без этого страница под шторкой скроллится пальцем, а на iOS ещё и
    // срабатывает pull-to-refresh поверх открытого меню.
    document.body.dataset.scrollLocked = ''
    return () => {
      el?.removeEventListener('keydown', listener)
      delete document.body.dataset.scrollLocked
    }
  }, [open])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 md:hidden">
      <button
        type="button"
        aria-label="Закрыть меню"
        onClick={onClose}
        className="absolute inset-0 bg-black/50"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Навигация"
        tabIndex={-1}
        className="absolute inset-y-0 left-0 flex w-72 max-w-[80vw] flex-col overscroll-contain border-r border-border-strong bg-bg-surface pb-safe shadow-lg focus:outline-none"
      >
        <div className="flex shrink-0 items-center justify-between px-5 py-5">
          <Link to={home} onClick={onClose} className="flex items-center text-text-primary">
            <EcosystemsLogo className="h-5" />
          </Link>
          <button
            type="button"
            aria-label="Закрыть меню"
            onClick={onClose}
            className="-mr-2 p-2 text-text-tertiary transition-colors hover:text-text-primary"
          >
            <Icon name="close" size={18} />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto pb-2">
          <NavSections
            items={items}
            renderItem={renderItem}
            onNavigate={onClose}
            ariaLabel="Меню"
          />
        </div>
      </div>
    </div>
  )
}
